// apps/regie/src/composants/calendrier/periodes.ts
// Le calcul des périodes du calendrier (semaine, mois, année) et le découpage
// des événements par jour. Tout se fait en JOURS CIVILS `AAAA-MM-JJ` à Paris :
// une chaîne se compare, s'additionne sur midi UTC (jamais d'heure d'été qui
// glisse d'un jour) et ne dépend pas du fuseau de l'ordinateur. Les semaines
// commencent le lundi. Pur et sans React : testé seul (`periodes.test.ts`).
import { locale, type Langue } from '@artosera/shared';

export type Jour = string;
export type VueCalendrier = 'liste' | 'semaine' | 'mois' | 'annee';
export const VUES_CALENDRIER: readonly VueCalendrier[] = ['liste', 'semaine', 'mois', 'annee'];

export const FUSEAU_CALENDRIER = 'Europe/Paris';
export const MINUTES_PAR_JOUR = 1440;
/** Durée prise pour un rendez-vous dont la fin est inconnue. */
export const DUREE_PAR_DEFAUT = 60;

const MS_PAR_JOUR = 86_400_000;
const FORMAT_JOUR = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Une chaîne `AAAA-MM-JJ` qui désigne un vrai jour du calendrier. */
export function estJour(valeur: string | null | undefined): valeur is Jour {
  if (!valeur) return false;
  const trouve = FORMAT_JOUR.exec(valeur);
  if (!trouve) return false;
  const date = midi(valeur);
  return !Number.isNaN(date.getTime()) && versJour(date) === valeur;
}

/** Midi UTC du jour civil : le point fixe de tous les calculs de dates. */
function midi(jour: Jour): Date {
  return new Date(`${jour}T12:00:00.000Z`);
}

function versJour(date: Date): Jour {
  return date.toISOString().slice(0, 10);
}

export function ajouterJours(jour: Jour, nombre: number): Jour {
  const date = midi(jour);
  date.setUTCDate(date.getUTCDate() + nombre);
  return versJour(date);
}

/** Écart en jours entiers : `ecartJours('2026-10-01', '2026-10-08') === 7`. */
export function ecartJours(de: Jour, a: Jour): number {
  return Math.round((midi(a).getTime() - midi(de).getTime()) / MS_PAR_JOUR);
}

/** 0 = lundi … 6 = dimanche. */
export function rangDansLaSemaine(jour: Jour): number {
  return (midi(jour).getUTCDay() + 6) % 7;
}

export function lundiDe(jour: Jour): Jour {
  return ajouterJours(jour, -rangDansLaSemaine(jour));
}

export function dimancheDe(jour: Jour): Jour {
  return ajouterJours(lundiDe(jour), 6);
}

export function debutDuMois(jour: Jour): Jour {
  return `${jour.slice(0, 8)}01`;
}

export function finDuMois(jour: Jour): Jour {
  const date = midi(debutDuMois(jour));
  date.setUTCMonth(date.getUTCMonth() + 1, 0);
  return versJour(date);
}

/** Le premier du mois, `nombre` mois plus loin (`-1` = précédent). */
export function ajouterMois(jour: Jour, nombre: number): Jour {
  const date = midi(debutDuMois(jour));
  date.setUTCMonth(date.getUTCMonth() + nombre, 1);
  return versJour(date);
}

export function debutDeLAnnee(jour: Jour): Jour {
  return `${jour.slice(0, 4)}-01-01`;
}

export function finDeLAnnee(jour: Jour): Jour {
  return `${jour.slice(0, 4)}-12-31`;
}

const FORMAT_PARIS = new Intl.DateTimeFormat('en-GB', {
  timeZone: FUSEAU_CALENDRIER,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** Le jour civil et la minute du jour (0–1439) d'un instant, à Paris. */
export function jourEtMinuteParis(instant: Date): { jour: Jour; minute: number } {
  const parties = Object.fromEntries(
    FORMAT_PARIS.formatToParts(instant).map((partie) => [partie.type, partie.value]),
  ) as Record<string, string>;
  const heure = Number(parties['hour']) % 24;
  return {
    jour: `${parties['year']}-${parties['month']}-${parties['day']}`,
    minute: heure * 60 + Number(parties['minute']),
  };
}

export function aujourdhuiParis(maintenant: Date = new Date()): Jour {
  return jourEtMinuteParis(maintenant).jour;
}

/** La plage de jours (inclus) que la vue affiche à partir de sa date d'ancrage. */
export function plageDeVue(vue: VueCalendrier, ancre: Jour): { du: Jour; au: Jour } {
  switch (vue) {
    case 'semaine':
      return { du: lundiDe(ancre), au: dimancheDe(ancre) };
    case 'mois':
      // La grille complète : les jours des mois voisins qui complètent les
      // semaines affichent aussi leurs événements.
      return { du: lundiDe(debutDuMois(ancre)), au: dimancheDe(finDuMois(ancre)) };
    case 'annee':
      return { du: debutDeLAnnee(ancre), au: finDeLAnnee(ancre) };
    case 'liste':
      return { du: debutDuMois(ancre), au: finDuMois(ancre) };
  }
}

/** Précédent (`-1`) ou suivant (`1`) : une semaine, un mois ou une année. */
export function deplacerAncre(vue: VueCalendrier, ancre: Jour, sens: -1 | 1): Jour {
  switch (vue) {
    case 'semaine':
      return ajouterJours(ancre, 7 * sens);
    case 'annee':
      return `${Number(ancre.slice(0, 4)) + sens}-01-01`;
    case 'mois':
    case 'liste':
      return ajouterMois(ancre, sens);
  }
}

/**
 * Découpe une plage en morceaux d'au plus `maxJours` jours : l'API de l'agenda
 * refuse une période de 92 jours ou plus, l'année se lit donc en quatre fois.
 */
export function decouperPlage(
  du: Jour,
  au: Jour,
  maxJours = 90,
): readonly { du: Jour; au: Jour }[] {
  const morceaux: { du: Jour; au: Jour }[] = [];
  let debut = du;
  while (debut <= au) {
    const fin = ajouterJours(debut, maxJours - 1);
    morceaux.push({ du: debut, au: fin < au ? fin : au });
    debut = ajouterJours(debut, maxJours);
  }
  return morceaux;
}

/** Les semaines (lundi → dimanche) de la grille d'un mois, jours des mois voisins compris. */
export function semainesDuMois(ancre: Jour): readonly (readonly Jour[])[] {
  const { du, au } = plageDeVue('mois', ancre);
  const semaines: Jour[][] = [];
  for (let lundi = du; lundi <= au; lundi = ajouterJours(lundi, 7)) {
    semaines.push(Array.from({ length: 7 }, (_, rang) => ajouterJours(lundi, rang)));
  }
  return semaines;
}

export function joursDeLaSemaine(ancre: Jour): readonly Jour[] {
  const lundi = lundiDe(ancre);
  return Array.from({ length: 7 }, (_, rang) => ajouterJours(lundi, rang));
}

/** Le jour 1 de chacun des douze mois de l'année de `ancre`. */
export function moisDeLAnnee(ancre: Jour): readonly Jour[] {
  return Array.from({ length: 12 }, (_, rang) => `${ancre.slice(0, 4)}-${pad(rang + 1)}-01`);
}

function pad(nombre: number): string {
  return String(nombre).padStart(2, '0');
}

/** `18:30` → 1110 ; `null` si la chaîne n'est pas une heure. */
export function minutesDeLHeure(heure: string | null | undefined): number | null {
  const trouve = /^(\d{1,2}):(\d{2})/.exec(heure ?? '');
  if (!trouve) return null;
  const minutes = Number(trouve[1]) * 60 + Number(trouve[2]);
  return minutes >= 0 && minutes < MINUTES_PAR_JOUR ? minutes : null;
}

/** 1110 → `18:30`. */
export function heureDeMinutes(minutes: number): string {
  return `${pad(Math.floor(minutes / 60) % 24)}:${pad(minutes % 60)}`;
}

// ── Titres, formatés dans la langue de la régie ──────────────────────────

function formatter(langue: Langue, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  // Les dates sont posées à midi UTC : le fuseau UTC les rend telles quelles.
  return new Intl.DateTimeFormat(locale(langue), { timeZone: 'UTC', ...options });
}

export function formaterJour(
  jour: Jour,
  langue: Langue,
  options: Intl.DateTimeFormatOptions,
): string {
  return formatter(langue, options).format(midi(jour));
}

/** « octobre 2026 », « 5 – 11 octobre 2026 », « 2026 » selon la vue. */
export function titrePeriode(vue: VueCalendrier, ancre: Jour, langue: Langue): string {
  if (vue === 'annee') return formaterJour(ancre, langue, { year: 'numeric' });
  if (vue === 'semaine') {
    const { du, au } = plageDeVue('semaine', ancre);
    return formatter(langue, { day: 'numeric', month: 'long', year: 'numeric' }).formatRange(
      midi(du),
      midi(au),
    );
  }
  return formaterJour(ancre, langue, { month: 'long', year: 'numeric' });
}

// ── Événements ───────────────────────────────────────────────────────────

export type TeinteCalendrier = 'accent' | 'neutral' | 'reserved' | 'available' | 'withdrawn';

/**
 * Ce que le calendrier sait afficher. `jourFin` est INCLUS. Sans `minuteDebut`,
 * l'événement dure la ou les journées entières (barre) ; avec, c'est un
 * rendez-vous à l'heure (`minuteFin` est alors une minute du `jourFin`, fin
 * exclue, ou `null` si la fin est inconnue).
 */
export interface EvenementCalendrier {
  readonly id: string;
  readonly titre: string;
  readonly jourDebut: Jour;
  readonly jourFin: Jour;
  readonly minuteDebut: number | null;
  readonly minuteFin: number | null;
  readonly teinte: TeinteCalendrier;
  /** Mis en évidence (le vernissage). */
  readonly evidence?: boolean;
  /** Une seconde ligne : le lieu, la nature. Aussi lue au lecteur d'écran. */
  readonly detail?: string | null;
  readonly href?: string;
  readonly onSelect?: () => void;
}

/** Un événement à l'heure, depuis des instants : tronque la fin à minuit pile. */
export function bornesDepuisInstants(
  debut: Date,
  fin: Date | null,
): Pick<EvenementCalendrier, 'jourDebut' | 'jourFin' | 'minuteDebut' | 'minuteFin'> {
  const d = jourEtMinuteParis(debut);
  if (!fin || fin.getTime() <= debut.getTime()) {
    return { jourDebut: d.jour, jourFin: d.jour, minuteDebut: d.minute, minuteFin: null };
  }
  const f = jourEtMinuteParis(fin);
  // Une fin à minuit pile appartient à la veille : l'intervalle est ouvert.
  if (f.minute === 0 && f.jour > d.jour) {
    return {
      jourDebut: d.jour,
      jourFin: ajouterJours(f.jour, -1),
      minuteDebut: d.minute,
      minuteFin: MINUTES_PAR_JOUR,
    };
  }
  return { jourDebut: d.jour, jourFin: f.jour, minuteDebut: d.minute, minuteFin: f.minute };
}

/** Un événement de journées entières, depuis des jours civils (fin incluse). */
export function bornesDepuisJours(
  debut: Jour,
  fin: Jour | null,
): Pick<EvenementCalendrier, 'jourDebut' | 'jourFin' | 'minuteDebut' | 'minuteFin'> {
  return {
    jourDebut: debut,
    jourFin: fin && fin > debut ? fin : debut,
    minuteDebut: null,
    minuteFin: null,
  };
}

/** Un morceau d'événement sur un seul jour : [debutMinute, finMinute[. */
export interface SegmentJour {
  readonly evenement: EvenementCalendrier;
  readonly jour: Jour;
  readonly debutMinute: number;
  readonly finMinute: number;
  /** L'événement a commencé la veille / continue le lendemain. */
  readonly debutCoupe: boolean;
  readonly finCoupee: boolean;
}

export function estJourneeEntiere(evenement: EvenementCalendrier): boolean {
  return evenement.minuteDebut === null;
}

/**
 * Découpe un événement à l'heure en un segment par jour couvert, borné à
 * [du, au]. Un rendez-vous 22:00 → 02:00 donne deux segments : le premier
 * jusqu'à 24:00, le second dès 00:00.
 */
export function segmenterParJour(
  evenement: EvenementCalendrier,
  du: Jour,
  au: Jour,
): readonly SegmentJour[] {
  const segments: SegmentJour[] = [];
  const debut = evenement.jourDebut > du ? evenement.jourDebut : du;
  const fin = evenement.jourFin < au ? evenement.jourFin : au;
  for (let jour = debut; jour <= fin; jour = ajouterJours(jour, 1)) {
    const premier = jour === evenement.jourDebut;
    const dernier = jour === evenement.jourFin;
    const entiere = estJourneeEntiere(evenement);
    const debutMinute = entiere || !premier ? 0 : (evenement.minuteDebut ?? 0);
    let finMinute = MINUTES_PAR_JOUR;
    if (!entiere && dernier) {
      finMinute = evenement.minuteFin ?? Math.min(debutMinute + DUREE_PAR_DEFAUT, MINUTES_PAR_JOUR);
    }
    segments.push({
      evenement,
      jour,
      debutMinute,
      finMinute: Math.max(finMinute, debutMinute),
      debutCoupe: !premier,
      finCoupee: !dernier,
    });
  }
  return segments;
}

function comparerSegments(a: SegmentJour, b: SegmentJour): number {
  return (
    a.debutMinute - b.debutMinute ||
    b.finMinute - a.finMinute ||
    a.evenement.titre.localeCompare(b.evenement.titre) ||
    a.evenement.id.localeCompare(b.evenement.id)
  );
}

/** Les rendez-vous à l'heure, rangés par jour puis par heure, dans [du, au]. */
export function segmentsALHeureParJour(
  evenements: readonly EvenementCalendrier[],
  du: Jour,
  au: Jour,
): ReadonlyMap<Jour, readonly SegmentJour[]> {
  const parJour = new Map<Jour, SegmentJour[]>();
  for (const evenement of evenements) {
    if (estJourneeEntiere(evenement)) continue;
    for (const segment of segmenterParJour(evenement, du, au)) {
      const liste = parJour.get(segment.jour) ?? [];
      liste.push(segment);
      parJour.set(segment.jour, liste);
    }
  }
  for (const liste of parJour.values()) liste.sort(comparerSegments);
  return parJour;
}

/** Une barre de journées entières sur une ligne de sept jours. */
export interface BarreSemaine {
  readonly evenement: EvenementCalendrier;
  /** Colonnes 0–6 (lundi → dimanche), bornes incluses. */
  readonly colonneDebut: number;
  readonly colonneFin: number;
  /** La rangée de la barre : les barres qui se chevauchent ne partagent pas la leur. */
  readonly voie: number;
  readonly debutCoupe: boolean;
  readonly finCoupee: boolean;
}

/**
 * Les barres d'une semaine (le lundi `lundi`) : un événement de journées
 * entières qui déborde de la semaine est coupé à ses bords, avec l'indication
 * qu'il continue.
 */
export function barresDeSemaine(
  evenements: readonly EvenementCalendrier[],
  lundi: Jour,
): readonly BarreSemaine[] {
  const dimanche = ajouterJours(lundi, 6);
  const candidats = evenements
    .filter(
      (evenement) =>
        estJourneeEntiere(evenement) &&
        evenement.jourDebut <= dimanche &&
        evenement.jourFin >= lundi,
    )
    .map((evenement) => ({
      evenement,
      colonneDebut: Math.max(0, ecartJours(lundi, evenement.jourDebut)),
      colonneFin: Math.min(6, ecartJours(lundi, evenement.jourFin)),
      debutCoupe: evenement.jourDebut < lundi,
      finCoupee: evenement.jourFin > dimanche,
    }))
    .sort(
      (a, b) =>
        a.colonneDebut - b.colonneDebut ||
        b.colonneFin - a.colonneFin ||
        a.evenement.titre.localeCompare(b.evenement.titre) ||
        a.evenement.id.localeCompare(b.evenement.id),
    );
  // Première voie libre : la dernière colonne occupée de chaque voie.
  const finsDeVoie: number[] = [];
  return candidats.map((candidat) => {
    let voie = finsDeVoie.findIndex((fin) => fin < candidat.colonneDebut);
    if (voie === -1) voie = finsDeVoie.length;
    finsDeVoie[voie] = candidat.colonneFin;
    return { ...candidat, voie };
  });
}

/** Combien d'événements couvrent chaque jour de [du, au] (un événement compte une fois par jour). */
export function densiteParJour(
  evenements: readonly EvenementCalendrier[],
  du: Jour,
  au: Jour,
): ReadonlyMap<Jour, number> {
  const compte = new Map<Jour, number>();
  for (const evenement of evenements) {
    const debut = evenement.jourDebut > du ? evenement.jourDebut : du;
    const fin = evenement.jourFin < au ? evenement.jourFin : au;
    for (let jour = debut; jour <= fin; jour = ajouterJours(jour, 1)) {
      compte.set(jour, (compte.get(jour) ?? 0) + 1);
    }
  }
  return compte;
}

/** Les événements qui touchent [du, au], sans doublon d'identifiant. */
export function evenementsDansLaPlage(
  evenements: readonly EvenementCalendrier[],
  du: Jour,
  au: Jour,
): readonly EvenementCalendrier[] {
  return evenements.filter((evenement) => evenement.jourDebut <= au && evenement.jourFin >= du);
}

export interface SegmentPlace extends SegmentJour {
  /** Colonne parmi `colonnes` (côte à côte quand les rendez-vous se chevauchent). */
  readonly colonne: number;
  readonly colonnes: number;
}

/**
 * Pose les segments d'un jour côte à côte : chaque groupe de rendez-vous qui
 * se chevauchent partage la largeur du jour à parts égales.
 */
export function placerSegments(segments: readonly SegmentJour[]): readonly SegmentPlace[] {
  const tries = [...segments].sort(comparerSegments);
  const places: SegmentPlace[] = [];
  let groupe: { segment: SegmentJour; colonne: number }[] = [];
  let finDuGroupe = -1;
  const vider = (): void => {
    const colonnes = groupe.reduce((max, { colonne }) => Math.max(max, colonne + 1), 1);
    for (const { segment, colonne } of groupe) places.push({ ...segment, colonne, colonnes });
    groupe = [];
  };
  const finsDeColonne: number[] = [];
  for (const segment of tries) {
    // Au moins 15 minutes de hauteur : un rendez-vous court reste cliquable.
    const fin = Math.max(segment.finMinute, segment.debutMinute + 15);
    if (segment.debutMinute >= finDuGroupe) {
      vider();
      finsDeColonne.length = 0;
      finDuGroupe = -1;
    }
    let colonne = finsDeColonne.findIndex((finColonne) => finColonne <= segment.debutMinute);
    if (colonne === -1) colonne = finsDeColonne.length;
    finsDeColonne[colonne] = fin;
    finDuGroupe = Math.max(finDuGroupe, fin);
    groupe.push({ segment, colonne });
  }
  vider();
  return places;
}
